import { useCallback, useState } from 'react';
import type { ConsultaDoBrik } from '../components/AssistentePensando';
import aviso, { textoDoErro } from '../lib/aviso';
import AssistenteService, {
    avisarAcaoDoAssistente,
    avisarConversasMudaram,
    type AcaoAssistente,
    type ContextoTelaAssistente,
    type ConversaAssistente,
    type MensagemAssistente,
    type OrigemPergunta,
    type RespostaPergunta,
} from '../services/AssistenteService';

/* Mensagem que só existe na tela enquanto a resposta não volta: a pergunta
   aparece na hora, sem esperar o servidor. O id negativo não colide com os
   ids reais e some quando a resposta chega. */
const idLocal = () => -Date.now();

/**
 * Uma conversa com o Brik: perguntar (com a resposta aparecendo enquanto o
 * modelo escreve), avaliar e acompanhar o cartão de ação.
 *
 * Saiu do `AssistentePage` em 07/10/2026, quando a Home passou a conversar no
 * próprio lugar (`docs/designsystem/propostas/home-brik.html`): a Home seria a
 * terceira cópia deste código, depois da tela cheia e do painel. O que é de
 * cada tela (trocar a URL, guardar a conversa do dia) fica com ela — o
 * `perguntar` devolve a resposta, ou nulo se falhou.
 */
export function useConversaAssistente() {
    const [conversa, setConversa] = useState<ConversaAssistente | null>(null);
    const [mensagens, setMensagens] = useState<MensagemAssistente[]>([]);
    const [perguntando, setPerguntando] = useState(false);
    /* O texto da resposta enquanto o modelo escreve (PerguntarStream). */
    const [parcial, setParcial] = useState('');
    /* O que o Brik está consultando agora (as frases do AssistentePensando). */
    const [consulta, setConsulta] = useState<ConsultaDoBrik | null>(null);
    const [degradadas, setDegradadas] = useState<Set<number>>(new Set());

    const perguntar = useCallback(async (
        texto: string,
        idExistente?: number,
        origem: OrigemPergunta = 'digitada',
        contexto?: ContextoTelaAssistente | null,
    ): Promise<RespostaPergunta | null> => {
        const pendente: MensagemAssistente = {
            mensagemId: idLocal(),
            papel: 'user',
            conteudo: texto,
            fontes: [],
            criadaEm: new Date().toISOString(),
        };

        setMensagens(anteriores => [...anteriores, pendente]);
        setParcial('');
        setConsulta(null);
        setPerguntando(true);

        try {
            const dados = await AssistenteService.perguntar(
                texto, idExistente, undefined, origem, contexto ?? null, setParcial,
                ferramentas => setConsulta(c => ({ ferramentas, vez: (c?.vez ?? 0) + 1 })),
            );

            setConversa(dados.conversa);
            /* Troca a mensagem otimista pela que o servidor gravou (id real),
               e acrescenta a resposta. */
            setMensagens(anteriores => [
                ...anteriores.filter(m => m.mensagemId !== pendente.mensagemId),
                dados.pergunta,
                dados.resposta,
            ]);

            if (dados.degradada) {
                setDegradadas(anteriores => new Set(anteriores).add(dados.resposta.mensagemId));
            }

            /* Ação que já saiu feita muda dados que outra aba ou o painel pode
               estar mostrando. */
            (dados.resposta.acoes ?? []).filter(a => a.estado === 'feita').forEach(avisarAcaoDoAssistente);

            avisarConversasMudaram();
            return dados;
        } catch (erro) {
            setMensagens(anteriores => anteriores.filter(m => m.mensagemId !== pendente.mensagemId));
            aviso.erro(textoDoErro(erro, 'Não foi possível responder agora.'));
            return null;
        } finally {
            setParcial('');
            setConsulta(null);
            setPerguntando(false);
        }
    }, []);

    /* Clicar no que já está marcado desfaz. A marca muda na hora e volta se a
       API recusar — esperar a resposta para acender o botão pareceria clique
       perdido. */
    const avaliar = useCallback(async (mensagem: MensagemAssistente, valor: 1 | -1) => {
        const anterior = mensagem.avaliacao ?? null;
        const nova = anterior === valor ? null : valor;
        const trocar = (v: 1 | -1 | null) => setMensagens(ms =>
            ms.map(m => (m.mensagemId === mensagem.mensagemId ? { ...m, avaliacao: v } : m)));

        trocar(nova);
        try {
            await AssistenteService.avaliar(mensagem.mensagemId, nova);
        } catch {
            trocar(anterior);
            aviso.erro('Não foi possível registrar a avaliação.');
        }
    }, []);

    /* Cartão de ação mudou de estado (confirmou, cancelou, desfez). */
    /* Troca a ação quando ela já está na mensagem e ACRESCENTA quando é nova —
       o "Mover para X" do cartão da revisão do funil cria a ação no clique, e
       ela precisa aparecer com o desfazer, como qualquer outra. */
    const trocarAcao = useCallback((mensagemId: number, nova: AcaoAssistente) =>
        setMensagens(ms => ms.map(m => {
            if (m.mensagemId !== mensagemId) return m;
            const acoes = m.acoes ?? [];
            return {
                ...m,
                acoes: acoes.some(a => a.acaoId === nova.acaoId)
                    ? acoes.map(a => (a.acaoId === nova.acaoId ? nova : a))
                    : [...acoes, nova],
            };
        })), []);

    return {
        conversa,
        setConversa,
        mensagens,
        setMensagens,
        perguntando,
        parcial,
        consulta,
        degradadas,
        perguntar,
        avaliar,
        trocarAcao,
    };
}
